import React, {ReactElement, ReactNode, useEffect, useRef, useState} from 'react';

export type InteractivePopoverProps = {
    children: ReactNode,
    content: ReactNode | string,
    direction: 'top' | 'bottom' | 'left' | 'right',
    width?: string,
    arrow?: boolean,
    "aria-label": string,
    className?: string,
    id?: string,
    closeDelayMs?: number,
};

const POPOVER_POSITION_BY_DIRECTION: Record<InteractivePopoverProps['direction'], React.CSSProperties> = {
    top: {bottom: 'calc(100% + 8px)', left: '50%', transform: 'translateX(-50%)'},
    bottom: {top: 'calc(100% + 8px)', left: '50%', transform: 'translateX(-50%)'},
    left: {right: 'calc(100% + 8px)', top: '50%', transform: 'translateY(-50%)'},
    right: {left: 'calc(100% + 8px)', top: '50%', transform: 'translateY(-50%)'}
};

const ARROW_CLASS_BY_DIRECTION: Record<InteractivePopoverProps['direction'], string> = {
    top: 'popover__arrow--bottom',
    bottom: 'popover__arrow--top',
    left: 'popover__arrow--right',
    right: 'popover__arrow--left'
};

let popoverIdCounter = 0;

const InteractivePopover = ({
    children,
    content,
    direction,
    width,
    arrow = true,
    className,
    id,
    "aria-label": ariaLabel,
    closeDelayMs = 120,
}: InteractivePopoverProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [fallbackId] = useState(() => {
        popoverIdCounter += 1;
        return `interactive-popover-${popoverIdCounter}`;
    });
    const resolvedPopoverId = id ?? fallbackId;

    const clearCloseTimer = () => {
        if (closeTimerRef.current) {
            clearTimeout(closeTimerRef.current);
            closeTimerRef.current = null;
        }
    };

    const openPopover = () => {
        clearCloseTimer();
        setIsOpen(true);
    };

    const closePopoverWithDelay = () => {
        clearCloseTimer();
        closeTimerRef.current = setTimeout(() => {
            setIsOpen(false);
        }, closeDelayMs);
    };

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const handleOutsideClick = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                clearCloseTimer();
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleOutsideClick);
        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
        };
    }, [isOpen]);

    useEffect(() => {
        return () => {
            clearCloseTimer();
        };
    }, []);

    const popoverClasses = [
        'popoverContainer__popover',
        'position-absolute',
        isOpen ? 'display-block' : 'display-none',
        'popover-skin--primary',
        arrow ? ARROW_CLASS_BY_DIRECTION[direction] : '',
        className ?? 'popover-text'
    ].filter(Boolean).join(' ');

    const handleBlurCapture = (event: React.FocusEvent<HTMLDivElement>) => {
        if (containerRef.current?.contains(event.relatedTarget as Node)) {
            return;
        }
        closePopoverWithDelay();
    };

    const mergeHandlers = <TEvent,>(
        originalHandler: ((event: TEvent) => void) | undefined,
        popoverHandler: (event: TEvent) => void
    ) => {
        return (event: TEvent) => {
            originalHandler?.(event);
            popoverHandler(event);
        };
    };

    const childProps = React.isValidElement(children)
        ? children.props as {[key: string]: unknown}
        : undefined;

    const triggerElement = React.isValidElement(children)
        ? React.cloneElement(children as ReactElement<{[key: string]: unknown}>, {
            onMouseEnter: mergeHandlers(childProps?.onMouseEnter as ((event: React.MouseEvent) => void) | undefined, openPopover),
            onMouseLeave: mergeHandlers(childProps?.onMouseLeave as ((event: React.MouseEvent) => void) | undefined, closePopoverWithDelay),
            onFocus: mergeHandlers(childProps?.onFocus as ((event: React.FocusEvent) => void) | undefined, openPopover),
            onBlur: mergeHandlers(childProps?.onBlur as ((event: React.FocusEvent) => void) | undefined, closePopoverWithDelay),
            onTouchStart: mergeHandlers(childProps?.onTouchStart as ((event: React.TouchEvent) => void) | undefined, openPopover),
            'aria-describedby': childProps?.['aria-describedby'] ?? resolvedPopoverId,
        })
        : (
            <button
                type="button"
                onMouseEnter={openPopover}
                onMouseLeave={closePopoverWithDelay}
                onFocus={openPopover}
                onBlur={closePopoverWithDelay}
                onTouchStart={openPopover}
                aria-describedby={resolvedPopoverId}
                className="popover-button"
            >
                {children}
            </button>
        );

    return (
        <div
            className="popoverContainer"
            ref={containerRef}
            onBlurCapture={handleBlurCapture}
        >
            <div
                id={resolvedPopoverId}
                aria-label={ariaLabel}
                role="tooltip"
                aria-hidden={!isOpen}
                className={popoverClasses}
                style={POPOVER_POSITION_BY_DIRECTION[direction]}
                onMouseEnter={openPopover}
                onMouseLeave={closePopoverWithDelay}
            >
                <div className="popover__content" style={width ? {width} : undefined}>
                    {content}
                </div>
            </div>
            <div className="display-inlineblock">
                {triggerElement}
            </div>
        </div>
    );
};

export default InteractivePopover;